/* =====================================================================
 * useAutoScroll — 입력창 리사이즈까지 따라가는 바닥 고정 스크롤
 *
 * 스트리밍/연속 메시지 수신:
 *   requestAnimationFrame 루프로 바닥을 부드럽게 추적
 *
 * 입력창 높이 변경/콘텐츠 리사이즈:
 *   ResizeObserver로 감지해 마지막 메시지가 입력창 아래로 숨지지 않게 유지
 *
 * 사용자 스크롤:
 *   위로 충분히 올리면 추적 해제, 다시 바닥으로 내려오면 자동 재개
 * ===================================================================== */

import { useRef, useEffect, useLayoutEffect, useCallback, useState } from 'react';

/** 바닥 근처 판정 임계값 (px) */
const NEAR_BOTTOM_PX = 120;

/** 이 px 이하면 즉시 스냅 (소수점 진동 방지) */
const SNAP_PX = 2;

/** 사용자가 의도적으로 위로 스크롤했다고 볼 최소 이동량 (px) */
const USER_SCROLL_BREAK_PX = 24;

/** 프로그램 스크롤 직후 scroll 이벤트는 무시하는 가드 시간 (ms) */
const PROGRAMMATIC_SCROLL_GUARD_MS = 96;

/** 비스트리밍 추적 유지 시간 (ms) */
const FOLLOW_WINDOW_MS = 600;

/** 리사이즈 후 바닥 추적 유지 시간 (ms) */
const RESIZE_FOLLOW_WINDOW_MS = 220;

/** 스트리밍 중 기본 LERP 비율 — 읽기 친화적 감속 */
const STREAMING_LERP = 0.12;

/** 비스트리밍에서 좀 더 빠르게 따라잡기 위한 LERP 비율 */
const IDLE_LERP = 0.22;

/** 프레임당 최소 이동량 (px) */
const MIN_STEP_PX = 1;

/**
 * 편안한 영역(COMFORT_LAG 이하)에서의 프레임당 최대 이동 (px).
 * ~150px/sec at 60fps → 약 6줄/sec → 빠른 읽기 속도와 유사.
 */
const STREAMING_COMFORT_SPEED = 2.5;

/** 비스트리밍에서 빠르게 바닥을 유지하기 위한 최대 이동량 (px) */
const IDLE_COMFORT_SPEED = 10;

/**
 * 이 갭 이하이면 편안한 읽기 속도로 스크롤.
 * ~300px ≈ 12-13줄의 여유.
 */
const COMFORT_LAG = 300;

/**
 * COMFORT_LAG를 넘어서면 점진적으로 가속.
 * 이 값에 도달하면 FAST_SPEED로 스크롤.
 */
const ACCEL_LAG = 700;

/**
 * 가속 영역에서의 프레임당 최대 이동 (px).
 * ~420px/sec → 빠르게 따라잡되 jump는 아님.
 */
const STREAMING_FAST_SPEED = 7;

/** 비스트리밍에서 더 빠르게 따라잡기 위한 최대 이동량 (px) */
const IDLE_FAST_SPEED = 18;

function nowMs() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function resolveScrollStep(diff: number, streaming: boolean) {
  const lerp = streaming ? STREAMING_LERP : IDLE_LERP;
  const comfortSpeed = streaming ? STREAMING_COMFORT_SPEED : IDLE_COMFORT_SPEED;
  const fastSpeed = streaming ? STREAMING_FAST_SPEED : IDLE_FAST_SPEED;

  let maxSpeed: number;
  if (diff <= COMFORT_LAG) {
    maxSpeed = comfortSpeed;
  } else {
    const t = Math.min((diff - COMFORT_LAG) / (ACCEL_LAG - COMFORT_LAG), 1);
    maxSpeed = comfortSpeed + t * (fastSpeed - comfortSpeed);
  }

  return Math.min(
    Math.max(diff * lerp, MIN_STEP_PX),
    maxSpeed,
  );
}

export function useAutoScroll(deps: unknown[], streaming = false, conversationId?: string) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const isNearBottomRef = useRef(true);
  const rafRef = useRef(0);

  /* streaming 값을 콜백 안에서 참조하기 위한 ref */
  const streamingRef = useRef(streaming);
  streamingRef.current = streaming;

  /**
   * rAF tick이 마지막으로 설정한 scrollTop 값.
   * auto-scroll은 scrollTop을 증가만 시키므로,
   * 현재 scrollTop이 이 값보다 충분히 작으면 유저가 위로 스크롤한 것.
   */
  const lastAutoScrollTop = useRef(0);

  /** 마지막 programmatic scroll 시간 */
  const lastProgrammaticScrollAt = useRef(0);

  /** 마지막으로 관찰한 컨테이너 높이 */
  const lastClientHeightRef = useRef(0);

  /**
   * auto-scroll 활성 여부.
   * 사용자가 위로 스크롤하면 false → 추적 정지.
   * 사용자가 다시 바닥으로 돌아오면 true → 추적 재개.
   */
  const engaged = useRef(true);

  /** 비스트리밍에서도 잠시 바닥 추적을 유지할 마감 시각 */
  const followUntilRef = useRef(0);

  /**
   * 대화 전환 후 최초 스크롤이 미완료인지 여부.
   * 히스토리 로딩 중 컨테이너가 미마운트거나 콘텐츠가 없을 때 true.
   */
  const pendingInitialScroll = useRef(false);

  const setNearBottom = useCallback((next: boolean) => {
    isNearBottomRef.current = next;
    setIsNearBottom((prev) => (prev === next ? prev : next));
  }, []);

  const stopTracking = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
  }, []);

  const jumpToBottom = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'instant' });
    lastAutoScrollTop.current = el.scrollTop;
    lastProgrammaticScrollAt.current = nowMs();
    lastClientHeightRef.current = el.clientHeight;
  }, []);

  const resolvePendingInitialScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el || !pendingInitialScroll.current || el.childElementCount === 0) return false;
    pendingInitialScroll.current = false;
    jumpToBottom();
    return true;
  }, [jumpToBottom]);

  /* ── 대화 전환 시 즉시 바닥으로 점프 (paint 전에 실행) ── */
  const prevConvId = useRef(conversationId);
  useLayoutEffect(() => {
    if (conversationId === prevConvId.current) return;
    prevConvId.current = conversationId;

    engaged.current = true;
    followUntilRef.current = 0;
    setNearBottom(true);
    stopTracking();

    const el = containerRef.current;
    if (!el) {
      pendingInitialScroll.current = true;
      return;
    }

    lastClientHeightRef.current = el.clientHeight;
    jumpToBottom();
    pendingInitialScroll.current = el.childElementCount === 0;
  }, [conversationId, jumpToBottom, setNearBottom, stopTracking]);

  /* ── rAF 기반 바닥 추적 루프 ── */
  const tick = useCallback((timestamp: number) => {
    const el = containerRef.current;
    if (!el) {
      rafRef.current = 0;
      return;
    }

    const target = Math.max(el.scrollHeight - el.clientHeight, 0);
    const diff = target - el.scrollTop;

    if (diff > SNAP_PX) {
      const step = resolveScrollStep(diff, streamingRef.current);
      el.scrollTop = Math.min(target, el.scrollTop + step);
      lastAutoScrollTop.current = el.scrollTop;
      lastProgrammaticScrollAt.current = timestamp;
    } else if (diff > 0) {
      el.scrollTop = target;
      lastAutoScrollTop.current = el.scrollTop;
      lastProgrammaticScrollAt.current = timestamp;
    }

    lastClientHeightRef.current = el.clientHeight;

    const stillOffBottom = target - el.scrollTop > SNAP_PX;
    if (
      engaged.current &&
      (streamingRef.current || timestamp < followUntilRef.current || stillOffBottom)
    ) {
      rafRef.current = requestAnimationFrame(tick);
      return;
    }

    rafRef.current = 0;
  }, []);

  const startTracking = useCallback((durationMs = FOLLOW_WINDOW_MS) => {
    if (!engaged.current) return;
    const deadline = nowMs() + durationMs;
    followUntilRef.current = Math.max(followUntilRef.current, deadline);
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  useEffect(() => () => stopTracking(), [stopTracking]);

  /* ── 스트리밍 시작/종료 시 추적 루프 제어 ── */
  useEffect(() => {
    if (streaming) {
      const el = containerRef.current;
      if (el) {
        const near =
          el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
        engaged.current = engaged.current || near || isNearBottomRef.current;
      } else {
        engaged.current = true;
      }

      if (engaged.current) {
        jumpToBottom();
        startTracking(FOLLOW_WINDOW_MS);
      }
      return;
    }

    const el = containerRef.current;
    if (el) {
      const near =
        el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
      setNearBottom(near);
    }
    if (engaged.current) {
      startTracking(FOLLOW_WINDOW_MS);
    }
  }, [jumpToBottom, setNearBottom, startTracking, streaming]);

  /* ── 비스트리밍: deps 변경 시 바닥 추적 ── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    lastClientHeightRef.current = el.clientHeight;

    if (pendingInitialScroll.current) {
      resolvePendingInitialScroll();
      return;
    }

    if (engaged.current || isNearBottomRef.current) {
      engaged.current = true;
      startTracking(FOLLOW_WINDOW_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  /* ── 콘텐츠/입력창 리사이즈 시에도 바닥 추적 유지 ── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      const target = containerRef.current;
      if (!target) return;
      lastClientHeightRef.current = target.clientHeight;
      if (pendingInitialScroll.current) {
        resolvePendingInitialScroll();
        return;
      }
      if (engaged.current || isNearBottomRef.current) {
        engaged.current = true;
        startTracking(RESIZE_FOLLOW_WINDOW_MS);
      }
    });

    observer.observe(el);
    const content = el.firstElementChild;
    if (content instanceof Element) {
      observer.observe(content);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, ...deps]);

  /* ── Public API ── */
  const scrollToBottom = useCallback(
    (smooth = true) => {
      engaged.current = true;
      setNearBottom(true);
      if (smooth) {
        startTracking(FOLLOW_WINDOW_MS);
      } else {
        jumpToBottom();
      }
    },
    [jumpToBottom, setNearBottom, startTracking],
  );

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const near =
      el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX;
    setNearBottom(near);

    const currentNow = nowMs();
    const clientHeightChanged =
      Math.abs(el.clientHeight - lastClientHeightRef.current) > SNAP_PX;
    lastClientHeightRef.current = el.clientHeight;
    const recentProgrammatic =
      currentNow - lastProgrammaticScrollAt.current < PROGRAMMATIC_SCROLL_GUARD_MS;

    if (recentProgrammatic || clientHeightChanged) {
      if (near) {
        engaged.current = true;
      }
      return;
    }

    if (engaged.current) {
      if (el.scrollTop < lastAutoScrollTop.current - USER_SCROLL_BREAK_PX && !near) {
        engaged.current = false;
        followUntilRef.current = 0;
        stopTracking();
        return;
      }
    }

    if (near) {
      engaged.current = true;
      lastAutoScrollTop.current = el.scrollTop;
      lastProgrammaticScrollAt.current = currentNow;
      if (streamingRef.current) {
        startTracking(RESIZE_FOLLOW_WINDOW_MS);
      }
    }
  }, [setNearBottom, startTracking, stopTracking]);

  return { containerRef, scrollToBottom, isNearBottom, handleScroll };
}
